// https://confluence.webedia-group.net/display/ads/wrapper+jad+v2
import {
  BRAND_HAS_GO_IP_ADSERVER,
  BRAND_HAS_STICKY_ADS,
  BRAND_HAS_TVTY,
  BRAND_HAS_DM_VAST_FALLBACK
} from 'common/configuration/constants';
import {
  DFP_BASE_URL,
  DISPLAY_PROVIDER,
  INHIBITION_AD_ID,
  newsContext,
  slideshowContext,
  longreadContext,
  STICKY_POS_HOLDER_CLASS,
  targetATF,
  targetBTF,
  targetMTF,
  targetPromo1,
  DM_PREMIUM_KEYWORD
} from 'common/constants/Ads';
import { MQ_MEDIUM } from 'common/constants/MediaQueries';
import {
  AdProviders,
  PostrollAsVideoConfig
} from 'common/module/advertisement/types';
import {
  filterAdsIdArray,
  getAdTrustContentHeight,
  getAdTrustVideoDurationStepTargeting,
  getBlueKaiKeywords,
  getMediarithmicsKeywords,
  getTagIdByGeoIp,
  prepareAdsPosition,
  triggerAdsPositionsReady,
  handleTvty
} from 'common/module/advertisement/utils';
import eventEmitter, { EventsTypes } from 'common/services/events/eventEmitter';
import loadJad from 'common/third_party/jad';
import { addClass, removeClass } from 'common/tools/dom/classes';
import { isStateBiggerThan } from 'common/tools/dom/mq-state';
import { isProd } from 'common/tools/env';
import { stringify } from 'common/tools/network/query-string';
import get from 'common/tools/objects/get';
import isNumeric from 'common/tools/string/isNumeric';
import trans from 'common/tools/translations/trans';
import { lazyLoadCall } from 'common/tools/ui/lazyload';
import { isHTMLElement, isString } from 'common/types';

import { PlayerCustomData } from 'website/module/player';

const CORRELATOR = Math.round(Math.random() * 10000000000);

let JAD_LIST_ID = window.JadIds; // list of ads id position
const JAD_CONFIG_PAGE = window.JadConfig; // object of all settings for the page (ads position config / ads position sizes / gpt tag / gpt keyword / ...)
const CMP_PAYWALL = JAD_CONFIG_PAGE?.cmpPaywall;
const ELEMENTS_MAPPING = JAD_CONFIG_PAGE?.adsMapping;
const GENERIC_UNIVERSE_SITE_SECTION = 'generic_universe';
const JAD_WEMASS = JAD_CONFIG_PAGE?.wemass;
const dataLayerJan = window.dataLayerJan;

const HIDE_PLACEHOLDER_HASH = [
  'jad_session_no_position=all',
  'jad_no_position=all'
];

// ADS Element and ID
const promo1Item = document.getElementsByClassName('js-autopromo')[0];
const inreadItem = document.getElementsByClassName('js-inread')[0];

let atfItem, mtfItem, btfItem;

if (BRAND_HAS_STICKY_ADS) {
  atfItem = document.getElementsByClassName('js-sticky-atf')[0];
  mtfItem = document.getElementsByClassName('js-sticky-mtf')[0];
  btfItem = document.getElementsByClassName('js-sticky-btf')[0];
} else {
  atfItem = document.getElementsByClassName('js-atf')[0];
  mtfItem = document.getElementsByClassName('js-mtf')[0];
  btfItem = document.getElementsByClassName('js-btf')[0];
}

const arrayTarget = [targetATF, targetMTF, targetBTF, targetPromo1];
const arrayPosition = [atfItem, mtfItem, btfItem, promo1Item]; // ADS position referer for changing position

const DFP_PULSE = 'dfp-pulse';

let dfpTagId = JAD_CONFIG_PAGE?.['tag-id'];
let dfpPageTag;
let contentHeightCase: number | boolean;

let jadInitialized = false;
let jadLoaded = false;

const setPageTargeting = async (targetingObj: JADTargeting) => {
  let targetingObject: JADTargeting = { ...targetingObj };

  // HASH KEYWORD FOR AD TESTING OR CLIENT DEMO
  const HASH_KEYWORD_TARGET = document.location.hash;
  if (HASH_KEYWORD_TARGET) {
    const keywords = HASH_KEYWORD_TARGET.split('#')[1].split('=') as Array<
      keyof JADTargeting
    >;

    if (keywords.length) {
      targetingObject[keywords[0]] = keywords[1];
    }
  }

  // Mediarithmics SEGMENTATION PART
  const MediarithmicsSegs = getMediarithmicsKeywords();

  targetingObject = {
    ...targetingObject,
    ...(MediarithmicsSegs.length ? { mics_webediaMics: MediarithmicsSegs } : {})
  };

  targetingObject = { ...targetingObject };

  // BLUEKAI SEGMENTATION PART
  const blukaiSegs = getBlueKaiKeywords();

  targetingObject = {
    ...targetingObject,
    ...(blukaiSegs ? { bluekai: [blukaiSegs] } : {})
  };

  // DAT content height case
  targetingObject = {
    ...targetingObject,
    ...(contentHeightCase
      ? { content_height_case: `${contentHeightCase}` }
      : {}),
    ...(window.bottom_ads_detected ? { bottom_ads_detected: 'true' } : {})
  };

  //TVTY
  if (BRAND_HAS_TVTY) {
    let tvtyData = null;
    await handleTvty().then(data => {
      tvtyData = data;
    });

    targetingObject = {
      ...targetingObject,
      ...(tvtyData ?? {})
    };
  }

  return targetingObject;
};

const getMappedPosition = (ids: string[], mapper?: JADAdsMapping) => {
  const pos: string[] = [];
  Object.entries(mapper ?? {}).forEach(([key, value]) => {
    if (value && ids.indexOf(value) !== -1) {
      pos.push(key);
    }
  });

  return pos;
};

const handleJadLoad = async (callback: (() => void) | null = null) => {
  // We have 2 use case: Display & Streaming.
  // This function wraps the load to handle JAD sdk load and necessary action once since display & streaming
  // can be called on the same page but separately...
  // We haven't done this in loadJad function (we could) to:
  // 1 keep the readability of the Jad handling in the same file
  // 2 keep the possibility to call loadJad somewhere else and not be blocked by the logic set here
  if (jadInitialized) {
    if (typeof callback === 'function') {
      callback();
    }

    return false;
  }

  // jadInitialized is set here to avoid a case where it could be load twice is Jad response being too long
  // or handleJadLoad called twice in a really short time (cf. trailer page with display & streaming)
  jadInitialized = true;

  // We split the dfpPageTag in 3 parts to handle the possibility of dynamically change the account id part with another account id
  // It's a need from the ES x Latam ads management but could be used for other stuff...
  if (
    BRAND_HAS_GO_IP_ADSERVER &&
    JAD_CONFIG_PAGE?.targeting?.site_section !== GENERIC_UNIVERSE_SITE_SECTION
  ) {
    dfpTagId = await getTagIdByGeoIp(dfpTagId);
  }

  const dfpTagIdDatas = dfpTagId?.split('/');

  const params = { network: dfpTagIdDatas?.[1], site: dfpTagIdDatas?.[2] };
  dfpPageTag = `${dfpTagId}${JAD_CONFIG_PAGE?.['tag-other-levels']}`;

  const targetingObj = await setPageTargeting({
    ...(JAD_CONFIG_PAGE?.targeting ? JAD_CONFIG_PAGE.targeting : {})
  });

  let config: PublisherConfig = {
    page: `${dfpPageTag}`,
    targeting: targetingObj
  };

  if (CMP_PAYWALL) {
    config = {
      ...config,
      cmp: {
        ...CMP_PAYWALL
      }
    };
  }

  if (JAD_WEMASS) {
    config = {
      ...config,
      wemass: {
        targeting: {
          page: {
            type: dataLayerJan?.site_route,
            content: {
              categories: [dataLayerJan?.content_category]
            },
            article: {
              id: dataLayerJan?.content_id,
              title: dataLayerJan?.content_title,
              description: dataLayerJan?.content_description ?? '',
              topics: JAD_CONFIG_PAGE?.targeting?.keywords ?? [],
              authors: [dataLayerJan?.content_author_name ?? []],
              publishedAt: dataLayerJan?.content_publication_date
                ? new Date(dataLayerJan.content_publication_date).toISOString()
                : '',
              premium: dataLayerJan?.content_premium ?? false,
              wordCount: dataLayerJan?.content_word_count ?? null,
              paragraphCount: dataLayerJan?.content_paragraph_count ?? null,
              section: dataLayerJan?.content_type ?? '',
              subsection: dataLayerJan?.content_sub_category ?? ''
            },
            user: {
              type: dataLayerJan?.content_user_type ?? '',
              age: dataLayerJan?.content_age ?? null,
              gender: dataLayerJan?.content_gender ?? ''
            }
          }
        }
      }
    };
  }

  if (JAD_LIST_ID?.length) {
    const interstitialTtl = isStateBiggerThan(MQ_MEDIUM) ? 21600 : 43200; // https://jira.webedia-group.net/browse/ALLOCINE-10018

    config = {
      ...config,
      debug: false,
      elementsMapping: { ...ELEMENTS_MAPPING },
      pagePositions: [...getMappedPosition(JAD_LIST_ID, ELEMENTS_MAPPING)],
      interstitialOnFirstPageEnabled: true,
      minRefreshTime: 5,
      autoRefreshEnabled: JAD_CONFIG_PAGE?.autoRefresh,
      hashedId: {
        sha256email: localStorage.getItem('_hid_')
      },
      cappingByPosition: JAD_CONFIG_PAGE?.customInterstitialCapping
        ? [{ position: 'interstitial', ttl: interstitialTtl, limit: 1 }]
        : null
    };
  }

  loadJad(params, () => {
    // We use the callback to immediately trigger a setConfig.
    // We can't (won't) know if it has been triggered by the player or the display
    // In case of display the setConfig will be reset and resend in startDisplayAds()
    // In case of player we don't need to resend a setConfig but only to do a createNewPositions
    if (!jadLoaded) {
      window.jad?.cmd?.push(() => {
        window.jad?.public?.setConfig(config);
        window.jad?.public?.loadPositions();
      });

      jadLoaded = true;
      eventEmitter.emit(EventsTypes.JAD_LOADED);
    }

    if (callback) {
      callback();
    }
  });
};

// Be advised that anchorArray cannot contain anchor that not matches the payload advertisementTargets.
// Also, the order of the item in the array has to be the same that in payload.advertisementTargets.
export const refreshAds = (
  payload: { advertisementTargets: string[] },
  anchorArray: string[] | boolean = []
) => {
  if (
    typeof window.jad === 'undefined' ||
    typeof window.jad.public === 'undefined'
  ) {
    return false;
  }

  const idsToRefresh: string[] = [];
  let mappedAdsToRefresh: string[] = [];

  if (payload.advertisementTargets.length) {
    // get the right ads to refresh and push it in an array (if Ads position is present on the page)
    payload.advertisementTargets.forEach(item => {
      const id = `dfp-${item}`;
      // ids are used to trigger HB refresh using callHeaderBidding
      idsToRefresh.push(id);
    });

    mappedAdsToRefresh = getMappedPosition(idsToRefresh, ELEMENTS_MAPPING);
  }

  if (Array.isArray(anchorArray) && anchorArray.length) {
    const tmpRefreshAd: string[] = [];
    anchorArray.forEach((anchor, index) => {
      tmpRefreshAd.push(`${mappedAdsToRefresh[index]}/${anchor}`);
    });

    mappedAdsToRefresh = tmpRefreshAd;
  }

  if (mappedAdsToRefresh.length) {
    window.jad.cmd?.push(() => {
      window.jad?.public?.refreshAds(mappedAdsToRefresh);
    });
  }
};

export const createPosition = (
  positionId: string,
  referenceElement: string | HTMLDivElement,
  forceCreateAnchor = true
) => {
  if (!positionId || !referenceElement) {
    return;
  }

  const item = `dfp-${positionId}`;
  let anchor = isString(referenceElement)
    ? referenceElement
    : referenceElement.id;

  if (forceCreateAnchor) {
    const element = document.createElement('div');
    element.id = item;
    if (isHTMLElement(referenceElement)) {
      referenceElement.appendChild(element);
    } else {
      document.getElementById(referenceElement)?.appendChild(element);
    }
    anchor = item;
  }

  window.jad?.cmd?.push(() => {
    window.jad?.public?.createNewPosition({
      name: getMappedPosition([item], ELEMENTS_MAPPING)[0],
      divId: anchor
    });
  });

  refreshAds({ advertisementTargets: [positionId] }, [anchor]);
};

const scrollInjectPosition = (positionId: string, addedClass: string) => {
  const addedAdAnchors = document.getElementsByClassName('js-added-ad');

  if (!addedAdAnchors.length) {
    return;
  }

  for (const elem of addedAdAnchors) {
    addClass(elem, addedClass);
  }

  const lazyLoadCallOptions = {
    // An offset rectangle applied to the root's bounding box when calculating intersections,
    // effectively shrinking or growing the root for calculation purposes. The value returned by
    // this property may not be the same as the one specified when calling the constructor as
    // it may be changed to match internal requirements. Each offset can be expressed in
    // pixels (px) or as a percentage (%). The default is "0px 0px 0px 0px".
    rootMargin: `${window.innerHeight * 2}px`
  };

  lazyLoadCall(
    [...addedAdAnchors],
    anchor => {
      createPosition(positionId, anchor.id, false);
    },
    lazyLoadCallOptions
  );
};

const startDisplayAds = () => {
  if (
    typeof window.jad === 'undefined' ||
    typeof window.jad.public === 'undefined'
  ) {
    return false;
  }

  window.jad.public.addEventListener('positionRenderEnded', e => {
    if (!e) {
      return;
    }

    const adElement = document.getElementById(e.slot.getSlotElementId());

    if (!adElement) {
      return;
    }

    if (!e.isEmpty && e.advertiserId !== INHIBITION_AD_ID) {
      addClass(adElement, 'filled');

      // We add the key to be used by css for text in placeholder
      if (
        ['dfp-atf', 'dfp-btf', 'dfp-mtf'].indexOf(e.slot.getSlotElementId()) !==
        -1
      ) {
        addClass(adElement, 'ad-titled');
        adElement.setAttribute('data-title', trans('ads_placeholder.title'));
      }

      if (
        e.slot.getSlotElementId() == 'dfp-masthead' &&
        adElement.parentElement
      ) {
        addClass(adElement.parentElement, 'children-filled');
      }
    } else if (adElement.parentElement?.parentElement) {
      removeClass(
        adElement.parentElement.parentElement,
        STICKY_POS_HOLDER_CLASS
      );
    }
  });

  triggerAdsPositionsReady();
  scrollInjectPosition('added-ad', 'added-ad');

  window.jad.cmd?.push(() => {
    window.jad?.public?.displayPositions();
  });
};

const setDisplayAds = async () => {
  // We define and handle each slot for the current page
  // The entry point is still the JAD_LIST_ID
  if (!JAD_LIST_ID || !JAD_LIST_ID.length || !JAD_CONFIG_PAGE) {
    if (!isProd()) {
      // eslint-disable-next-line no-console
      console.warn(
        'DFP setDisplayAds error either JAD_LIST_ID or JAD_CONFIG_PAGE is missing or empty'
      );
    }

    return false;
  }

  // Get content height (only for newspage for the moment => Digital Ad Trust Compliency)
  contentHeightCase = getAdTrustContentHeight([
    document.getElementById('article-container'),
    document.getElementById('article-title')
  ]);

  // We move ads for mobile
  prepareAdsPosition(
    {
      news: newsContext,
      slideshow: slideshowContext,
      longread: longreadContext
    },
    inreadItem,
    arrayPosition,
    arrayTarget
  );
};

// this init the display, not the streaming...
export const init = (resolve: () => void) => {
  if (
    DISPLAY_PROVIDER !== AdProviders.JAD_SYSTEM_CODE ||
    !JAD_LIST_ID ||
    !JAD_LIST_ID.length
  ) {
    if (!isProd()) {
      // eslint-disable-next-line no-console
      console.warn(
        'Jad or "wrapper V2" (using Dfp adserver) display init not engaged'
      );
    }
    resolve();
    return false;
  }

  window.jad = window.jad || {};
  window.jad.cmd = window.jad.cmd || [];

  // load jad sdk and do setConfig and loadPosition as soon as possible
  // (done in handleJadLoad)
  // keep in mind that handleJadLoad can be / is also called for the videoAds positions
  handleJadLoad();

  function isNoPosition() {
    let hide =
      window.sessionStorage.getItem('jad_session_no_position') === 'all';

    if (hide) {
      return true;
    }

    HIDE_PLACEHOLDER_HASH.forEach(hash => {
      if (document.location.hash.indexOf(hash) !== -1) {
        hide = true;
        return;
      }
    });

    return hide;
  }

  // We check if the hash is present to hide all ad placeholders
  if (isNoPosition()) {
    addClass(document.body, 'jad-no-position');
  }

  eventEmitter.on(EventsTypes.ASYNCHRONOUS_PAGE_CHANGE, refreshAds);

  if (newsContext && isStateBiggerThan(MQ_MEDIUM)) {
    JAD_LIST_ID = filterAdsIdArray(JAD_LIST_ID, [DFP_PULSE]);
  }

  setDisplayAds().then(() => {
    // We resolve before loading Jad to avoid weird issue with the players.
    // Indeed, players are launched on ads_ready event, and we can't wait too long to load it.
    // To keep a separate way to handle things and use Jad preroll, we also load Jad on
    // getVideoAdslUrl() if Jad isn't présent on the page (embed or diff between resolve and Jad load)
    resolve();

    // after all setting are done, check if jad is loaded or wait for jad to be loaded to trigger
    // ads display
    if (jadLoaded) {
      startDisplayAds();
    } else {
      eventEmitter.on(EventsTypes.JAD_LOADED, startDisplayAds);
    }
  });
};

export const removePosition = (
  positionId?: string,
  referenceElement?: HTMLDivElement | null
) => {
  if (!positionId || !referenceElement || !referenceElement.firstChild) {
    return;
  }

  const child = referenceElement.firstChild;
  if (isHTMLElement(child)) {
    const divId = child.id;
    const positionName = getMappedPosition([divId], ELEMENTS_MAPPING)[0];

    window.jad?.cmd?.push(() => {
      window.jad?.public?.removePosition(`${positionName}/${divId}`);
    });

    refreshAds({ advertisementTargets: [positionId] }, [divId]);
  }
};

const getFallbackVastUrl = (config: JADConfig, adPosition: string) => {
  const cust_params = ['fallback=true'];

  if (config.targeting) {
    for (const [key, value] of Object.entries(config.targeting)) {
      cust_params.push(`${key}=${value}`);
    }
  }

  if (!BRAND_HAS_DM_VAST_FALLBACK) {
    // the patern is : /ADUNIT_ID/ADUNIT_TOP_LEVEL/SUBLEVEL (encodé) + / (pas encodé) + key1=value&key2=value1,value2
    const dfpAdunitTag = `${config['tag-id']}${config['tag-other-levels']}`;

    return `${encodeURIComponent(dfpAdunitTag)}/${encodeURIComponent(
      cust_params.join('&')
    )}`;
  } else {
    const queryString = stringify({
      url: window.location.href,
      correlator: CORRELATOR,
      description_url: window.location.href,
      vpos: adPosition,
      iu: `${dfpTagId}${config['tag-other-levels']}`,
      sz: BRAND_HAS_DM_VAST_FALLBACK
        ? '480x360|480x361|480x270|640x480|640x360'
        : '480x360|480x361',
      env: 'vp',
      gdfp_req: '1',
      impl: 's',
      output: 'vast',
      unviewed_position_start: 1,
      cust_params: cust_params.join('&')
    });

    return `${DFP_BASE_URL}${queryString ? '?' + queryString : ''}`;
  }
};

const getJadForVideoAds = () =>
  new Promise<void>(resolve => {
    if (!jadInitialized) {
      handleJadLoad(() => {
        resolve();
      });
    } else {
      resolve();
    }
  });

////////////////////////////// HANDLE VIDEO ADS PARTS //////////////////////////////////////
const getVideoAdslUrl = async (
  adPosition: string,
  playerPosition: string | undefined,
  targetingFromVideoData: Targeting | undefined
): Promise<string | boolean> => {
  let playerPositionName = playerPosition;

  if (!playerPosition) {
    playerPositionName = 'playercontent';
  }

  return new Promise(resolve => {
    // handle video targeting
    const videoAdsName = `${adPosition}_${playerPositionName}`;

    if (!JAD_CONFIG_PAGE || !dfpTagId) {
      resolve(false);
      return;
    }

    if (JAD_CONFIG_PAGE['tag-other-levels'] === '/embed') {
      const url = getFallbackVastUrl(JAD_CONFIG_PAGE, adPosition);
      resolve(url);
      return;
    }

    getJadForVideoAds().then(() => {
      const positionAdskey = `${videoAdsName}/${videoAdsName}`;

      window.jad?.cmd?.push(() => {
        window.jad?.public?.setTargetingOnPosition(videoAdsName, {
          ...targetingFromVideoData
        });

        window.jad?.public?.getDailymotionAdsParamsForScript(
          [`${positionAdskey}`],
          adsParams => {
            resolve(adsParams[`${positionAdskey}`]);
          }
        );
      });
    });
  });
};

export const getPrerollConfiguration = async (data: PlayerCustomData) => {
  let tag: string | boolean = false;
  const firstVideo = get(data, ['videos', '0']);

  if (data.partner && data.partner.preRoll === false) {
    return { tag: DM_PREMIUM_KEYWORD };
  }

  const targetingFromVideoData: Targeting = {};
  const videoDuration = get(firstVideo, ['duration']);

  const videoDurationSplit =
    videoDuration && typeof videoDuration === 'string'
      ? videoDuration.split(':')
      : null;
  let duration: number | null = null;

  if (videoDurationSplit) {
    duration =
      +videoDurationSplit[0] * 60 * 60 +
      +videoDurationSplit[1] * 60 +
      +videoDurationSplit[2];
  } else if (videoDuration && !videoDurationSplit) {
    duration = parseInt(videoDuration, 10);
  }

  if (isNumeric(duration)) {
    targetingFromVideoData['duration_steps'] = [
      `${getAdTrustVideoDurationStepTargeting(duration)}`
    ];
  }

  try {
    tag = await getVideoAdslUrl(
      'preroll',
      data.player_position,
      targetingFromVideoData
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('getPrerollConfiguration error : ', e);
  }

  return { tag };
};

export const getPostrollConfiguration = async (data: PlayerCustomData) => {
  let tag: string | boolean = false;

  if (data.partner && !data.partner.postRoll) {
    return { tag: DM_PREMIUM_KEYWORD };
  }

  try {
    tag = await getVideoAdslUrl('postroll', data.player_position, {});
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('getPostrollConfiguration error : ', e);
  }

  return { tag };
};

export const getClientConfiguration = () => ({
  system: 'wbads',
  client: 'googima',
  adscheduleid: 'randomstring',
  vpaidcontrols: true, // Asked by the traffic team, put here because we don't want to "pollute" other cases than DFP
  vpaidmode: 'insecure' // Asked by the traffic team, put here because we don't want to "pollute" other cases than DFP cf. ALLOCINE-6894
});

const setTargetingOnPosition = (data: PlayerCustomData) => {
  if (data.videos?.length) {
    const video = data.videos[0];

    const targeting: Targeting = {};

    const nationality = (video.countries ?? []).reduce<string[]>(
      (acc, country) => (country?.id ? [...acc, `${country.id}`] : acc),
      []
    );

    if (nationality.length) {
      targeting['nationality'] = nationality;
    }

    if (video.genresIds && video.genresIds.length) {
      targeting['genre'] = video.genresIds.map(id => `${id}`);
    }

    if (video.relatedEntityId) {
      targeting[`${video.relatedEntityType}_id`] = [`${video.relatedEntityId}`];
    }

    window.jad?.public?.setTargetingOnPosition('trailer', targeting);
  }
};

const getTrailers = (resolve: (value: PostrollAsVideoConfig) => void) => {
  let resolved = false;
  const resolveTrailers = (trailers?: Array<JADTrailers>) => {
    if (!resolved) {
      resolved = true;
      resolve({
        type: 'jad',
        postroll: trailers ?? []
      });
    }
  };

  const timeout = window.setTimeout(resolveTrailers, 5000);

  if (JAD_CONFIG_PAGE && JAD_CONFIG_PAGE['tag-other-levels'] === '/embed') {
    window.jad?.public?.loadPositions();
    window.jad?.public?.displayPositions();
  }

  window.jad?.public?.getTrailers(trailers => {
    clearTimeout(timeout);
    resolveTrailers(trailers);
  });
};

// Main function
export const getPostrollAsVideoConfiguration = async (
  data: PlayerCustomData
): Promise<PostrollAsVideoConfig> => {
  if (data.partner && !data.partner.postRoll) {
    return Promise.resolve({});
  }

  // targeting => does genre / nationality / movieId / seriesId  / etc ... on data ?
  return new Promise(resolve => {
    getJadForVideoAds().then(() => {
      window.jad?.cmd?.push(() => {
        setTargetingOnPosition(data);
        getTrailers(resolve);
      });
    });
  });
};
